/**
 * Character-related utility functions
 * Shared utilities for character management across components
 */

import type { CharacterInfo } from '@gofranz/gpt-muse-common';

/**
 * Get the image path for a character
 * @param characterId - The unique identifier for the character
 * @returns The path to the character's image file
 */
export const getCharacterImagePath = (characterId: string): string => {
  return `/characters/${characterId}.png`;
};

/**
 * Check if a character ID represents a custom character
 * @param characterId - The character identifier to check
 * @returns True if this is a custom character
 */
export const isCustomCharacter = (characterId: string | null | undefined): boolean => {
  return characterId === 'custom';
};

/**
 * Check if a character ID represents no character selection
 * @param characterId - The character identifier to check
 * @returns True if no character is selected
 */
export const isNoCharacter = (characterId: string | null | undefined): boolean => {
  return characterId === null || characterId === undefined;
};

/**
 * Get display text for a character selection
 * @param selectedCharacter - The character ID
 * @param characters - Array of available characters
 * @returns Display text for the character
 */
export const getCharacterDisplayText = (
  selectedCharacter: string | null,
  characters: CharacterInfo[] = []
): string => {
  if (selectedCharacter === null) {
    return 'No character';
  }
  if (selectedCharacter === 'custom') {
    return '✏️ Custom prompt';
  }
  const character = characters.find(c => c.id === selectedCharacter);
  return character ? character.name : 'Select Character';
};

/**
 * Get tooltip text for a character selection
 * @param selectedCharacter - The character ID
 * @param characters - Array of available characters
 * @returns Tooltip text for the character
 */
export const getCharacterTooltipText = (
  selectedCharacter: string | null,
  characters: CharacterInfo[] = []
): string => {
  if (selectedCharacter === null) {
    return 'No character selected';
  }
  if (selectedCharacter === 'custom') {
    return 'Using custom system prompt';
  }
  const character = characters.find(c => c.id === selectedCharacter);
  return character ? character.name : 'Select a character';
};